/**
 * Money is always an integer number of poisha (1 BDT = 100 poisha).
 * Quantities are integers in thousandths of the base unit ("milli units").
 * Rates are integers in basis points (1% = 100 bps).
 * Floating point is never used to store or accumulate these values.
 */

export type Poisha = number;
export type MilliQty = number;
export type BasisPoints = number;

export const QTY_SCALE = 1000;
export const BPS_SCALE = 10_000;

export class MoneyError extends Error {
  override name = "MoneyError";
}

export function assertPoisha(value: number): asserts value is Poisha {
  if (!Number.isSafeInteger(value)) {
    throw new MoneyError(`Not a valid poisha amount: ${value}`);
  }
}

/** Divides a BigInt numerator by a positive BigInt divisor, rounding half away from zero. */
function divRoundHalfUp(numerator: bigint, divisor: bigint): bigint {
  const negative = numerator < 0n;
  const abs = negative ? -numerator : numerator;
  const quotient = (abs * 2n + divisor) / (divisor * 2n);
  return negative ? -quotient : quotient;
}

function toSafeNumber(value: bigint): number {
  const result = Number(value);
  if (!Number.isSafeInteger(result)) {
    throw new MoneyError("Amount exceeds the safe integer range");
  }
  return result;
}

/** Line amount for a quantity (milli units) at a unit price (poisha per base unit). */
export function lineAmount(qty: MilliQty, unitPrice: Poisha): Poisha {
  assertPoisha(qty);
  assertPoisha(unitPrice);
  return toSafeNumber(divRoundHalfUp(BigInt(qty) * BigInt(unitPrice), BigInt(QTY_SCALE)));
}

/** Applies a rate in basis points to an amount, rounding half up. */
export function applyRate(amount: Poisha, rate: BasisPoints): Poisha {
  assertPoisha(amount);
  assertPoisha(rate);
  return toSafeNumber(divRoundHalfUp(BigInt(amount) * BigInt(rate), BigInt(BPS_SCALE)));
}

/**
 * Splits a tax-inclusive amount into its net and tax parts:
 * net = gross * 10000 / (10000 + rate), tax = gross - net.
 */
export function splitInclusive(gross: Poisha, rate: BasisPoints): { net: Poisha; tax: Poisha } {
  assertPoisha(gross);
  assertPoisha(rate);
  const net = toSafeNumber(
    divRoundHalfUp(BigInt(gross) * BigInt(BPS_SCALE), BigInt(BPS_SCALE + rate)),
  );
  return { net, tax: gross - net };
}

/** Allocates an amount across weights without losing or creating a poisha (largest remainder). */
export function allocate(amount: Poisha, weights: readonly number[]): Poisha[] {
  assertPoisha(amount);
  if (weights.length === 0) return [];
  const total = weights.reduce((sum, w) => {
    if (!Number.isSafeInteger(w) || w < 0) throw new MoneyError(`Invalid weight: ${w}`);
    return sum + w;
  }, 0);
  if (total === 0) throw new MoneyError("Weights must not all be zero");

  const big = BigInt(amount);
  const bigTotal = BigInt(total);
  const shares = weights.map((w) => (big * BigInt(w)) / bigTotal);
  let remainder = big - shares.reduce((a, b) => a + b, 0n);
  const abs = (v: bigint) => (v < 0n ? -v : v);
  const order = weights
    .map((w, i) => ({ i, rem: abs((big * BigInt(w)) % bigTotal) }))
    .sort((a, b) => (a.rem === b.rem ? a.i - b.i : a.rem > b.rem ? -1 : 1));
  const step = remainder >= 0n ? 1n : -1n;
  for (const { i } of order) {
    if (remainder === 0n) break;
    shares[i] = (shares[i] ?? 0n) + step;
    remainder -= step;
  }
  return shares.map(toSafeNumber);
}

/** Rounds a final bill to the tenant's cash rounding rule (0 = none, 50 = ৳0.50, 100 = ৳1). */
export function roundCash(amount: Poisha, step: 0 | 50 | 100): Poisha {
  assertPoisha(amount);
  if (step === 0) return amount;
  return toSafeNumber(divRoundHalfUp(BigInt(amount), BigInt(step)) * BigInt(step));
}

const DECIMAL_PATTERN = /^(-)?(\d{1,13})(?:\.(\d{1,2}))?$/;

/** Parses a user-entered taka string ("1,250.5", "১২৫০.৫০") into poisha. Rejects more than 2 decimals. */
export function parseTaka(input: string): Poisha {
  const normalized = fromBanglaDigits(input).replace(/[,\s৳]/g, "");
  const match = DECIMAL_PATTERN.exec(normalized);
  if (!match) throw new MoneyError(`Invalid amount: ${input}`);
  const [, sign, whole = "0", fraction = ""] = match;
  const value = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  assertPoisha(value);
  return sign ? -value : value;
}

const BANGLA_DIGITS = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"] as const;

export function toBanglaDigits(input: string): string {
  return input.replace(/\d/g, (d) => BANGLA_DIGITS[Number(d)] ?? d);
}

export function fromBanglaDigits(input: string): string {
  return input.replace(/[০-৯]/g, (d) => String(d.charCodeAt(0) - 0x09e6));
}

/** Groups an integer string the South Asian way: 12,34,567. */
function groupLakh(digits: string): string {
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3);
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",")},${last3}`;
}

export interface FormatMoneyOptions {
  digits?: "latn" | "beng";
  symbol?: boolean;
  /** Show paisa even when zero. Defaults to true. */
  fraction?: boolean;
}

/** Formats poisha as BDT with lakh/crore grouping, e.g. ৳12,34,567.50 or ৳১২,৩৪,৫৬৭.৫০. */
export function formatMoney(amount: Poisha, options: FormatMoneyOptions = {}): string {
  assertPoisha(amount);
  const { digits = "latn", symbol = true, fraction = true } = options;
  const negative = amount < 0;
  const abs = Math.abs(amount);
  const taka = groupLakh(String(Math.floor(abs / 100)));
  const paisa = String(abs % 100).padStart(2, "0");
  let text = fraction || paisa !== "00" ? `${taka}.${paisa}` : taka;
  if (digits === "beng") text = toBanglaDigits(text);
  return `${negative ? "-" : ""}${symbol ? "৳" : ""}${text}`;
}

/** Formats milli units as a decimal quantity without trailing zeros, e.g. 1500 → "1.5". */
export function formatQty(qty: MilliQty, digits: "latn" | "beng" = "latn"): string {
  assertPoisha(qty);
  const negative = qty < 0;
  const abs = Math.abs(qty);
  const whole = Math.floor(abs / QTY_SCALE);
  const frac = String(abs % QTY_SCALE)
    .padStart(3, "0")
    .replace(/0+$/, "");
  const text = `${negative ? "-" : ""}${groupLakh(String(whole))}${frac ? `.${frac}` : ""}`;
  return digits === "beng" ? toBanglaDigits(text) : text;
}
